import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { StorageMonitorService } from './storageMonitorService.service';
import { LogoutService, LogoutReason } from './logout.service';
import { BroadcastChannelService } from '../broadcast-channel.service';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private tokenExpirationTimer: any;
  private inactivityTimer: any;
  private checkInterval: any;
  private loginCompleted = new BehaviorSubject<boolean>(false);
  loginCompleted$ = this.loginCompleted.asObservable();
  userEmail: any;

  constructor(
    private http: HttpClient,
    private router: Router,
    private storageMonitorService: StorageMonitorService,
    private logoutService: LogoutService,
    private broadcastChannelService: BroadcastChannelService
  ) {
    this.startMonitoring();
  }
  urlApiManagement = environment.urlApiManagement;

  login(userEmail: any) {
    const url = `${this.urlApiManagement}/api/v1/token/login`;
    const body = {
      userEmail: userEmail
    };
    this.userEmail = userEmail;
    this.http.post(url, body).subscribe({
      next: (response: any) => {
        localStorage.setItem('mlct', response.access_token);
        this.setInactivityTimer();
        this.storageMonitorService.startMonitoring();
        this.setRenewalTimer();
        this.loginCompleted.next(true);
      },
      error: () => {
        this.loginCompleted.next(false);
        this.logout('token_expired');
      }
    });
  }

  private setInactivityTimer() {
    this.clearInactivityTimer();
    this.inactivityTimer = setTimeout(() => {
      this.checkActivity();
    }, 120000); 
  }

  private clearInactivityTimer() {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
    }
  }

  private setRenewalTimer() {
    this.tokenExpirationTimer = setInterval(() => {
      if (localStorage.getItem('mlct')) {
        this.renewToken();
      } else {
        this.logout('token_expired');
      }
    }, 60000); // 1 minuto
  }

  private clearRenewalTimer() {
    if (this.tokenExpirationTimer) {
      clearInterval(this.tokenExpirationTimer);
    }
  }

  private clearInterval() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
    }
  }

  logout(reason: LogoutReason = 'manual', skipBroadcast: boolean = false) {
    const currentUrl = this.router.url || window.location.pathname + window.location.search + window.location.hash;

    if (!skipBroadcast) {
      if (reason !== 'manual') {
        try {
          this.logoutService.setReturnUrl(currentUrl);
          this.broadcastChannelService.notifyLogoutInitiated(reason, currentUrl);
        } catch (e) {
          console.warn('Could not capture return URL before logout', e);
        }
      } else {
        this.logoutService.clearReturnUrl();
        this.broadcastChannelService.notifyLogoutInitiated(reason, null);
      }
    } else if (reason !== 'manual') {
      // If we are reacting to a broadcast, we still want to save OUR local URL before redirecting
      this.logoutService.setReturnUrl(currentUrl);
    } else {
      this.logoutService.clearReturnUrl();
    }

    this.clearInactivityTimer();
    this.clearInterval();
    this.clearRenewalTimer();
    this.storageMonitorService.stopMonitoring();
    this.logoutService.logout(reason);
  }

  renewToken() {
    const url = `${this.urlApiManagement}/api/v1/token/refresh`;
    const body = {
      token: localStorage.getItem('mlct'),
      userEmail: this.userEmail
    };
    this.http.post(url, body).subscribe({
      next: (response: any) => {
        localStorage.setItem('mlct', response.access_token);
        this.setInactivityTimer();
      },
      error: () => {
        this.logout('token_expired');
      }
    });
  }

  startMonitoring() {
    this.checkInterval = setInterval(() => {
      this.checkActivity();
    }, 1000);
  }

  private checkActivity() {
    const lastActivity = parseInt(localStorage.getItem('lastActivity') || '0', 10);
    const currentTime = new Date().getTime();
    if (lastActivity === 0 || currentTime - lastActivity > 3600000) {
      this.logout('inactive');
    } else {
      this.setInactivityTimer();
    }
  }
}
